import { z } from 'zod';
import {
  affiliateApplicationSchema,
  affiliateApplicationResponseSchema,
  apiErrorSchema,
  contactRequestSchema,
  contactResponseSchema,
  publicProductListSchema,
  quoteRequestSchema,
  quoteRequestResponseSchema,
  referralCodeLookupSchema,
  type AffiliateApplicationInput,
  type ContactInput,
  type ContactResponse,
  type PublicProductList,
  type QuoteRequestInput,
  type QuoteRequestResponse,
} from '@mopani/contracts';

/**
 * `@mopani/contracts` exports an inferred type for every OTHER response shape
 * in this file, but not for the affiliate application response or the
 * referral lookup — see packages/contracts/src/affiliateApi.ts. This file must
 * not edit that package (it is commercial policy, not frontend code), so the
 * types are inferred locally with the same `z.infer` the package itself uses.
 */
type AffiliateApplicationResponse = z.infer<typeof affiliateApplicationResponseSchema>;
type ReferralCodeLookup = z.infer<typeof referralCodeLookupSchema>;

/**
 * Typed API client for the public site.
 *
 * The deployed July bundle contained ZERO calls to the API (AUD3-004). Every
 * public feature that appeared to work was inert. This module is the entire
 * network surface of the public site — if a feature is not here, it does not
 * talk to the backend, and the UI must not pretend otherwise.
 *
 * Responses are parsed with the shared contract schemas rather than cast, so a
 * backend/frontend shape mismatch surfaces immediately instead of rendering
 * `undefined` into the page.
 */

/**
 * Where the API lives.
 *
 * Same-origin by default: nginx proxies /api/ to the API container on both the
 * admin and public hosts, so a relative URL always reaches it.
 *
 * This used to default to https://api.mopani-suppliers.store. That subdomain
 * has no valid certificate, so the browser refused the connection before the
 * request left the machine and every call failed as "could not reach the API" —
 * and because the fallback only applied when VITE_API_BASE_URL was unset at
 * BUILD time, the whole app broke silently the first time someone built it
 * without that variable. A default that works with no configuration is the
 * point; the variable remains an override for pointing a dev server at a
 * different backend.
 */
const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export class ApiRequestError extends Error {
  status: number;
  fieldErrors?: Record<string, string[]>;

  constructor(status: number, message: string, fieldErrors?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    // Network-level failure: offline, DNS, TLS, CORS preflight rejection.
    throw new ApiRequestError(0, 'We could not reach our server. Please check your connection and try again.');
  }

  const text = await res.text();
  const body: unknown = text ? safeJson(text) : null;

  if (!res.ok) {
    const parsed = apiErrorSchema.safeParse(body);
    if (parsed.success) {
      throw new ApiRequestError(res.status, parsed.data.error, parsed.data.details);
    }
    throw new ApiRequestError(res.status, `Request failed (${res.status}).`);
  }
  return body;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** POST /api/quote — the core revenue mechanism. Closes AUD3-004. */
export async function submitQuoteRequest(input: QuoteRequestInput): Promise<QuoteRequestResponse> {
  const payload = quoteRequestSchema.parse(input);
  const body = await request('/api/quote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return quoteRequestResponseSchema.parse(body);
}

/** POST /api/contact — NEW endpoint. Closes AUD3-005. */
export async function submitContact(input: ContactInput): Promise<ContactResponse> {
  const payload = contactRequestSchema.parse(input);
  const body = await request('/api/contact', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return contactResponseSchema.parse(body);
}

/** GET /api/products — NEW endpoint. Closes GATE1-D3 / A-4. */
export async function fetchProducts(category?: string): Promise<PublicProductList> {
  const qs = category && category !== 'all' ? `?category=${encodeURIComponent(category)}` : '';
  const body = await request(`/api/products${qs}`);
  return publicProductListSchema.parse(body);
}

/**
 * POST /api/affiliate/apply — NEW endpoint. Affiliate/partner programme
 * applications (MS-V2-AFF-001). Not account creation: the response is a
 * pending application, reviewed by staff, who then issue portal credentials
 * by hand. See Partners.tsx for why that distinction is shown to the user.
 */
export async function submitAffiliateApplication(
  input: AffiliateApplicationInput,
): Promise<AffiliateApplicationResponse> {
  const payload = affiliateApplicationSchema.parse(input);
  const body = await request('/api/affiliate/apply', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return affiliateApplicationResponseSchema.parse(body);
}

/**
 * GET /api/affiliate/referral/:code — NEW endpoint. Resolves a `?ref=` code
 * from an affiliate's own referral link to a first name, so an applicant can
 * see "Referred by X" before submitting. Unauthenticated by design (first name
 * only — see referralCodeLookupSchema's comment in affiliateApi.ts), and
 * callers must treat a rejection here as non-fatal: this lookup is a nicety,
 * never a gate on submitting the application.
 */
export async function lookupReferralCode(code: string): Promise<ReferralCodeLookup> {
  const body = await request(`/api/affiliate/referral/${encodeURIComponent(code)}`);
  return referralCodeLookupSchema.parse(body);
}

/**
 * POST /api/quote/attachments — NEW endpoint. Closes AUD3-006.
 * Uploads land on a private volume and are never served from a web root.
 */
export async function uploadQuoteAttachment(file: File): Promise<{ id: string; filename: string }> {
  const form = new FormData();
  form.append('file', file);
  const body = await request('/api/quote/attachments', { method: 'POST', body: form });
  return body as { id: string; filename: string };
}
